import React, { useState } from 'react';
import axios from 'axios';
import { ArrowRight, CheckCircle, Send } from 'lucide-react';
import { getStoredAttribution } from '../../utils/attribution';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const BUDGETS = ['Under $5k', '$5k – $15k', '$15k – $50k', '$50k+', 'Not sure yet'];

export default function BrandInquiryForm({ openLaunch }) {
  const [form, setForm] = useState({ name: '', email: '', company: '', message: '', budget: '' });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');

  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError(''); setLoading(true);
    try {
      await axios.post(`${API}/brand-inquiries`, { ...form, attribution: getStoredAttribution(), source: 'home_inquiry_form' });
      setDone(true);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError((Array.isArray(detail) ? detail[0]?.msg : detail) || 'Something went wrong — please try again or email jon@freddymedia.com');
    } finally { setLoading(false); }
  };

  return (
    <section id="get-proposal" className="py-20 sm:py-28 bg-[#0B1138] border-y border-white/5" data-testid="brand-inquiry-section">
      <div className="max-w-5xl mx-auto px-6 sm:px-8 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-start">
          {/* Left — pitch */}
          <div>
            <p className="text-[10px] sm:text-xs font-mono uppercase tracking-[0.3em] text-[#FF4343] mb-3">★ Not ready for a call? ★</p>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight leading-tight mb-5">
              Get a <span className="text-[#FF4343]">custom proposal</span> in 24 hours.
            </h2>
            <p className="text-sm sm:text-base text-[#9CA3AF] leading-relaxed mb-6">
              Tell us what you&apos;re promoting and we&apos;ll send back a scoped plan — which executives
              and creators fit your ICP, what the campaign mix looks like, and exactly what it costs.
              No call required, no commitment.
            </p>
            <ul className="space-y-2.5">
              {['Hand-picked executive & creator matches for your category', 'Transparent pricing — endorsements, amplification, production', 'Reply lands in your inbox within one business day'].map((b, i) => (
                <li key={i} className="text-xs sm:text-sm text-[#9CA3AF] flex items-start gap-2.5">
                  <span className="text-[#FF4343] mt-0.5 flex-shrink-0">✓</span>{b}
                </li>
              ))}
            </ul>
          </div>

          {/* Right — form */}
          <div className="bg-[#111827] border border-white/10 p-6 sm:p-8">
            {done ? (
              <div className="text-center py-10" data-testid="inquiry-success">
                <CheckCircle className="w-12 h-12 text-[#10B981] mx-auto mb-4" />
                <h3 className="font-display text-xl font-bold mb-2">Got it — proposal incoming.</h3>
                <p className="text-sm text-[#9CA3AF] mb-6">We&apos;ll reply to <span className="text-white">{form.email}</span> within one business day.</p>
                {openLaunch && (
                  <button type="button" onClick={() => openLaunch('inquiry_success')} data-testid="inquiry-success-book-btn"
                    className="rounded-full border-2 border-[#FF4343] text-[#FF4343] hover:bg-[#FF4343] hover:text-[#090E33] px-6 py-2.5 text-xs font-bold inline-flex items-center gap-2 transition-all">
                    Want to move faster? Book a call <ArrowRight className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-4" data-testid="brand-inquiry-form">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="text-[10px] font-mono uppercase text-[#6B7280] block mb-1.5">Your name *</label>
                    <input required value={form.name} onChange={set('name')} data-testid="inquiry-name-input"
                      className="w-full px-3 py-2.5 bg-[#090E33] border border-white/10 text-white text-sm focus:outline-none focus:border-[#FF4343]" placeholder="Jane Smith" />
                  </div>
                  <div>
                    <label className="text-[10px] font-mono uppercase text-[#6B7280] block mb-1.5">Work email *</label>
                    <input required type="email" value={form.email} onChange={set('email')} data-testid="inquiry-email-input"
                      className="w-full px-3 py-2.5 bg-[#090E33] border border-white/10 text-white text-sm focus:outline-none focus:border-[#FF4343]" placeholder="jane@company.com" />
                  </div>
                </div>
                <div>
                  <label className="text-[10px] font-mono uppercase text-[#6B7280] block mb-1.5">Company *</label>
                  <input required value={form.company} onChange={set('company')} data-testid="inquiry-company-input"
                    className="w-full px-3 py-2.5 bg-[#090E33] border border-white/10 text-white text-sm focus:outline-none focus:border-[#FF4343]" placeholder="Acme Inc." />
                </div>
                <div>
                  <label className="text-[10px] font-mono uppercase text-[#6B7280] block mb-1.5">What are you promoting?</label>
                  <textarea rows={3} value={form.message} onChange={set('message')} data-testid="inquiry-message-input"
                    className="w-full px-3 py-2.5 bg-[#090E33] border border-white/10 text-white text-sm focus:outline-none focus:border-[#FF4343] resize-none"
                    placeholder="e.g. B2B SaaS for auto dealerships — want exec endorsements + LinkedIn ads" />
                </div>
                <div>
                  <label className="text-[10px] font-mono uppercase text-[#6B7280] block mb-1.5">Rough budget (optional)</label>
                  <select value={form.budget} onChange={set('budget')} data-testid="inquiry-budget-select"
                    className="w-full px-3 py-2.5 bg-[#090E33] border border-white/10 text-white text-sm focus:outline-none focus:border-[#FF4343]">
                    <option value="">Select…</option>
                    {BUDGETS.map(b => <option key={b} value={b}>{b}</option>)}
                  </select>
                </div>
                {error && <p className="text-xs text-red-400" data-testid="inquiry-error">{error}</p>}
                <button type="submit" disabled={loading} data-testid="inquiry-submit-btn"
                  className="w-full rounded-full border-2 border-[#FF4343] bg-[#FF4343] text-[#090E33] hover:bg-[#E03030] hover:border-[#E03030] py-3 text-sm font-bold inline-flex items-center justify-center gap-2 transition-all disabled:opacity-60">
                  {loading ? 'Sending…' : <>Get my proposal <Send className="w-4 h-4" /></>}
                </button>
                <p className="text-[10px] text-[#6B7280] text-center">No spam, no sales sequence — one human reply from the Freddy team.</p>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
